import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your email address...');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setMessage('Invalid verification link. Token is missing.');
      setVerificationStatus('error');
      return;
    }

    const verifyEmail = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/auth/verify-email`, { token });
        setMessage(
          'Your email has been verified successfully. Redirecting you to login...'
        );
        setVerificationStatus('success');
        setTimeout(() => navigate('/'), 3000);
      } catch (err) {
        setMessage(
          err.response?.data?.message ||
            'Verification failed. The link may be expired or invalid.'
        );
        setVerificationStatus('error');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0e1d3a 0%, #11224d 100%)',
        px: 2
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          bgcolor: '#ffffff'
        }}
      >
        <MarkEmailReadIcon
          sx={{
            fontSize: 48,
            color: 'warning.main',
            mb: 1
          }}
        />

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 1
          }}
        >
          Email Verification
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mb: 3 }}
        >
          ShipperDock Account Security
        </Typography>

        {verificationStatus === 'verifying' && (
          <>
            <CircularProgress sx={{ color: 'warning.main', mb: 2 }} />
            <Typography sx={{ color: 'text.secondary' }}>
              {message}
            </Typography>
          </>
        )}

        {verificationStatus !== 'verifying' && (
          <>
            <Alert
              severity={verificationStatus === 'success' ? 'success' : 'error'}
              sx={{ mb: 3, textAlign: 'left' }}
            >
              {message}
            </Alert>

            <Button
              component={Link}
              to="/"
              fullWidth
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                py: 1.2,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'secondary.main'
                }
              }}
            >
              Go to Login
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EmailVerification;
